import { effect, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ServicioInactividad } from '@core/services/inactividad/inactividad.service';
import { SesionFacade } from '@core/store/sesion/sesion.facade';

export const CLAVE_INICIO_SESION_ABSOLUTA = 'onboarding_inicio_sesion_absoluta';

const MINUTOS_SESION_ABSOLUTA = 60;
const SEGUNDOS_POR_MINUTO = 60;
const MS_POR_SEGUNDO = 1000;
export const DURACION_SESION_ABSOLUTA_MS = MINUTOS_SESION_ABSOLUTA * SEGUNDOS_POR_MINUTO * MS_POR_SEGUNDO;

interface MarcaSesionAbsoluta {
  sessionId: string;
  inicio: number;
}

@Injectable({ providedIn: 'root' })
export class ExpiracionAbsolutaSesionService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly sesionFacade = inject(SesionFacade);
  private readonly inactividadService = inject(ServicioInactividad);

  public activar(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    effect(() => {
      this.sincronizarConSessionId(this.sesionFacade.sessionId());
    });
  }

  private sincronizarConSessionId(sessionId: string | null): void {
    if (!sessionId) {
      this.inactividadService.detenerAbsoluto();
      this.eliminarMarca();
      return;
    }

    const marca = this.leerMarca();
    if (marca && marca.sessionId === sessionId) {
      this.reanudarDesdeMarca(marca);
      return;
    }

    this.iniciarNuevaSesion(sessionId);
  }

  private reanudarDesdeMarca(marca: MarcaSesionAbsoluta): void {
    const msRestantes = DURACION_SESION_ABSOLUTA_MS - (Date.now() - marca.inicio);
    this.inactividadService.iniciarAbsoluto(msRestantes);
  }

  private iniciarNuevaSesion(sessionId: string): void {
    this.guardarMarca({ sessionId, inicio: Date.now() });
    this.inactividadService.iniciarAbsoluto(DURACION_SESION_ABSOLUTA_MS);
  }

  private leerMarca(): MarcaSesionAbsoluta | null {
    try {
      const marcaGuardada = sessionStorage.getItem(CLAVE_INICIO_SESION_ABSOLUTA);
      return marcaGuardada ? (JSON.parse(marcaGuardada) as MarcaSesionAbsoluta) : null;
    } catch {
      return null;
    }
  }

  private guardarMarca(marca: MarcaSesionAbsoluta): void {
    try {
      sessionStorage.setItem(CLAVE_INICIO_SESION_ABSOLUTA, JSON.stringify(marca));
    } catch {
      /* Ignorar errores de guardado en storage */
    }
  }

  private eliminarMarca(): void {
    try {
      sessionStorage.removeItem(CLAVE_INICIO_SESION_ABSOLUTA);
    } catch {
      /* Ignorar errores de eliminación en storage */
    }
  }
}
